import { motion, AnimatePresence } from "framer-motion";
import { X, Camera, Upload, Image as ImageIcon } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import type { UserProfile } from "../lib/constants/profile";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  userProfile: UserProfile;
  onSave: (profile: UserProfile) => void;
}

const EditProfileModal = ({ isOpen, onClose, userProfile, onSave }: EditProfileModalProps) => {
  const [profile, setProfile] = useState<UserProfile>(userProfile);
  const [activeTab, setActiveTab] = useState<'basic' | 'preferences' | 'financial'>('basic');
  const [showImageModal, setShowImageModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  useEffect(() => {
    return () => {
      // Cleanup: stop camera stream when component unmounts
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [stream]);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfile({
          ...profile,
          avatar: e.target?.result as string
        });
        setShowImageModal(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
      setIsCameraActive(true);
    } catch (err) {
      console.error("Error accessing camera:", err);
      alert("Unable to access camera. Please make sure you've granted camera permissions.");
    }
  };

  const capturePhoto = () => {
    if (videoRef.current) {
      const canvas = document.createElement('canvas');
      const { videoWidth, videoHeight } = videoRef.current;
      const size = Math.min(videoWidth, videoHeight);
      const startX = (videoWidth - size) / 2;
      const startY = (videoHeight - size) / 2;
      
      canvas.width = size;
      canvas.height = size;
      
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          videoRef.current,
          startX, startY, size, size,
          0, 0, size, size
        );
        const photoUrl = canvas.toDataURL('image/jpeg', 0.8);
        setProfile({
          ...profile,
          avatar: photoUrl
        });
        stopCamera();
        setShowImageModal(false);
      }
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
    setIsCameraActive(false);
  };

  // Image Upload Modal with enhanced camera view
  const ImageUploadModal = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={() => {
        stopCamera();
        setShowImageModal(false);
      }}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-md w-full shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {isCameraActive ? 'Take Your Photo' : 'Update Profile Picture'}
          </h3>
          <button
            onClick={() => {
              stopCamera();
              setShowImageModal(false);
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isCameraActive ? (
          <div className="space-y-4">
            <div className="relative aspect-square rounded-lg overflow-hidden bg-black">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                className="absolute inset-0 w-full h-full object-cover mirror"
                style={{ transform: 'scaleX(-1)' }}
              />
              <div className="absolute inset-0 border-2 border-white/30 rounded-lg pointer-events-none" />
            </div>
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={capturePhoto}
                className="px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition flex items-center gap-2"
              >
                <Camera className="w-5 h-5" />
                Take Photo
              </button>
              <button
                onClick={stopCamera}
                className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-white rounded-full hover:bg-gray-300 dark:hover:bg-gray-600 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-green-500 dark:hover:border-green-500 transition flex flex-col items-center gap-3 group"
            >
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full group-hover:bg-green-500/10 transition">
                <Upload className="w-8 h-8 text-gray-400 group-hover:text-green-500 transition" />
              </div>
              <span className="text-gray-600 dark:text-gray-300 group-hover:text-green-500 transition">
                Upload from device
              </span>
            </button>
            
            <button
              onClick={startCamera}
              className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-green-500 dark:hover:border-green-500 transition flex flex-col items-center gap-3 group"
            >
              <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-full group-hover:bg-green-500/10 transition">
                <Camera className="w-8 h-8 text-gray-400 group-hover:text-green-500 transition" />
              </div>
              <span className="text-gray-600 dark:text-gray-300 group-hover:text-green-500 transition">
                Take a photo
              </span>
            </button>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageUpload}
              accept="image/*"
              className="hidden"
            />
          </div>
        )}
      </motion.div>
    </motion.div>
  );

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setProfile(userProfile);
    }
  }, [isOpen, userProfile]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Handle background click
  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(profile);
    onClose();
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'preferences', label: 'Preferences' },
    { id: 'financial', label: 'Financial' }
  ];

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white dark:bg-gray-800 rounded-3xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
          onClick={e => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>

          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Edit Profile
            </h2>
          </div>

          {/* Tabs */}
          <div className="flex space-x-4 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 rounded-lg transition ${
                  activeTab === tab.id
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {activeTab === 'basic' && (
              <>
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <img
                      src={profile.avatar}
                      alt={profile.name}
                      className="w-32 h-32 rounded-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => setShowImageModal(true)}
                      className="absolute bottom-0 right-0 p-2 bg-green-500 rounded-full text-white hover:bg-green-600 transition"
                    >
                      <Camera className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      value={profile.name}
                      onChange={e => setProfile({ ...profile, name: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      value={profile.email}
                      onChange={e => setProfile({ ...profile, email: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Phone</label>
                    <input
                      type="tel"
                      value={profile.phone}
                      onChange={e => setProfile({ ...profile, phone: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Location</label>
                    <input
                      type="text"
                      value={profile.location}
                      onChange={e => setProfile({ ...profile, location: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Bio</label>
                    <textarea
                      value={profile.bio}
                      onChange={e => setProfile({ ...profile, bio: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'preferences' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Email Notifications</label>
                  <input
                    type="checkbox"
                    checked={profile.preferences.emailNotifications}
                    onChange={e => setProfile({
                      ...profile,
                      preferences: {
                        ...profile.preferences,
                        emailNotifications: e.target.checked
                      }
                    })}
                    className="w-5 h-5 rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Push Notifications</label>
                  <input
                    type="checkbox"
                    checked={profile.preferences.pushNotifications}
                    onChange={e => setProfile({
                      ...profile,
                      preferences: {
                        ...profile.preferences,
                        pushNotifications: e.target.checked
                      }
                    })}
                    className="w-5 h-5 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Monthly Reports</label>
                  <input
                    type="checkbox"
                    checked={profile.preferences.monthlyReports}
                    onChange={e => setProfile({
                      ...profile,
                      preferences: {
                        ...profile.preferences,
                        monthlyReports: e.target.checked
                      }
                    })}
                    className="w-5 h-5 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Two-Factor Authentication</label>
                  <input
                    type="checkbox"
                    checked={profile.preferences.twoFactorAuth}
                    onChange={e => setProfile({
                      ...profile,
                      preferences: {
                        ...profile.preferences,
                        twoFactorAuth: e.target.checked
                      }
                    })}
                    className="w-5 h-5 rounded"
                  />
                </div>
              </div>
            )}

            {activeTab === 'financial' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Annual Income</label>
                  <input
                    type="number"
                    value={profile.financialProfile.income}
                    onChange={e => setProfile({
                      ...profile,
                      financialProfile: {
                        ...profile.financialProfile,
                        income: Number(e.target.value)
                      }
                    })}
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Savings Goal</label>
                  <input
                    type="number"
                    value={profile.financialProfile.savingsGoal}
                    onChange={e => setProfile({
                      ...profile,
                      financialProfile: {
                        ...profile.financialProfile,
                        savingsGoal: Number(e.target.value)
                      }
                    })}
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Risk Tolerance</label>
                  <select
                    value={profile.financialProfile.riskTolerance}
                    onChange={e => setProfile({
                      ...profile,
                      financialProfile: {
                        ...profile.financialProfile,
                        riskTolerance: e.target.value as 'low' | 'medium' | 'high'
                      }
                    })}
                    className="w-full px-4 py-2 rounded-lg border dark:bg-gray-700 dark:border-gray-600"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-4 mt-8 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {showImageModal && <ImageUploadModal />}
      </AnimatePresence>
    </>
  );
};

export default EditProfileModal; 